import { useState } from 'react';
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import UserAvatar from '@/components/UserAvatar';
import ReviewCard from '@/components/ReviewCard';
import FollowButton from '@/components/FollowButton';
import InviteModal from '@/components/InviteModal';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Star, Users, UserPlus, ChevronRight, ChevronDown, ChevronUp,
  Utensils, Search, MapPin, TrendingUp, Heart, UtensilsCrossed, Trophy, Crown
} from 'lucide-react';
import BottomNavigation from '@/components/BottomNavigation';

function getMediaUrl(path: string): string {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  if (path.startsWith('/api/')) return path;
  if (path.startsWith('data:')) return path;
  const cleanPath = path.startsWith('public/') ? path.substring(7) : path;
  return `/api/media/public/${cleanPath}`;
}

function MyNetworkSection() {
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(false);

  const networkUserId = user?.canonicalId || user?.id;
  const { data: followingData, isLoading: loadingFollowing } = useQuery<{ users: any[], total: number }>({
    queryKey: [`/api/users/${networkUserId}/following`],
    enabled: !!networkUserId,
  });

  const { data: followersData, isLoading: loadingFollowers } = useQuery<{ users: any[], total: number }>({
    queryKey: [`/api/users/${networkUserId}/followers`],
    enabled: !!networkUserId,
  });

  const followingCount = followingData?.total || 0;
  const followersCount = followersData?.total || 0;
  const following = followingData?.users || [];

  if (loadingFollowing || loadingFollowers) {
    return (
      <div className="px-4 py-3">
        <div className="flex items-center gap-4">
          <Skeleton className="w-20 h-8 rounded-md" />
          <Skeleton className="w-20 h-8 rounded-md" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-3">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between gap-2 group"
        data-testid="button-my-network"
      >
        <div className="flex items-center gap-1.5">
          <Users className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold">My Network</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <span className="text-sm font-bold">{followingCount}</span>
            <span className="text-xs text-muted-foreground">Following</span>
          </div>
          <div className="w-px h-3 bg-border" />
          <div className="flex items-center gap-1">
            <span className="text-sm font-bold">{followersCount}</span>
            <span className="text-xs text-muted-foreground">Followers</span>
          </div>
          {expanded
            ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
            : <ChevronDown className="w-4 h-4 text-muted-foreground" />
          }
        </div>
      </button>

      {expanded && (
        <div className="mt-3" data-testid="network-following-list">
          {following.length > 0 ? (
            <div className="space-y-1">
              {following.map((person: any) => (
                <div
                  key={person.id}
                  className="flex items-center gap-3 p-2 rounded-md hover-elevate cursor-pointer"
                  onClick={() => navigate(`/profile/${person.id}`)}
                >
                  <UserAvatar
                    src={person.avatar}
                    name={person.displayName || person.username}
                    size="sm"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {person.displayName || person.username}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {person.reviewsCount || 0} reviews
                    </p>
                  </div>
                  <FollowButton
                    targetUserId={person.id}
                    size="sm"
                    variant="outline"
                    showText={true}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground">You're not following anyone yet</p>
              <p className="text-xs text-muted-foreground mt-1">Follow people below to see their reviews here</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DiscoverPeopleSection() {
  const [, navigate] = useLocation();

  const { data: suggestionsData, isLoading } = useQuery<{ users: any[] }>({
    queryKey: ['/api/users/suggestions?limit=10'],
  });

  const suggestions = suggestionsData?.users || [];

  if (isLoading) {
    return (
      <div className="px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-sm">Discover People</h3>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex-shrink-0 w-44">
              <Card>
                <CardContent className="p-3 flex flex-col items-center gap-2">
                  <Skeleton className="w-12 h-12 rounded-full" />
                  <Skeleton className="w-24 h-3" />
                  <Skeleton className="w-20 h-3" />
                  <Skeleton className="w-28 h-3" />
                  <Skeleton className="w-full h-8 rounded-md" />
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (suggestions.length === 0) return null;

  return (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm">Discover People</h3>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
        {suggestions.map((user: any) => {
          const reasonLine = user.favoriteCuisine
            ? `Loves ${user.favoriteCuisine}`
            : user.topRestaurant
              ? `Fan of ${user.topRestaurant.name}`
              : user.topTags?.length > 0
                ? `Into ${user.topTags[0]}`
                : `${user.reviewsCount || 0} reviews`;

          return (
            <div key={user.id} className="flex-shrink-0 w-44">
              <Card className="hover-elevate h-full">
                <CardContent className="p-3 flex flex-col items-center gap-2 h-full">
                  <div
                    className="cursor-pointer"
                    onClick={() => navigate(`/profile/${user.id}`)}
                  >
                    <UserAvatar
                      src={user.avatar}
                      name={user.displayName || user.username}
                      size="lg"
                      showTrustBadge={true}
                      trustScore={user.trustScore || 75}
                    />
                  </div>
                  <div className="text-center w-full min-w-0">
                    <p
                      className="text-sm font-medium truncate cursor-pointer hover:underline"
                      onClick={() => navigate(`/profile/${user.id}`)}
                    >
                      {user.displayName || user.username}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {user.reviewsCount || 0} reviews
                    </p>
                  </div>

                  <p className="text-xs text-center text-primary font-medium truncate w-full" title={reasonLine}>
                    {reasonLine}
                  </p>

                  {user.topRestaurant && user.favoriteCuisine && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground truncate w-full justify-center">
                      <MapPin className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{user.topRestaurant.name}</span>
                    </div>
                  )}

                  {user.topTags && user.topTags.length > 0 && (
                    <div className="flex flex-wrap gap-1 justify-center">
                      {user.topTags.slice(0, 2).map((tag: string) => (
                        <Badge key={tag} variant="secondary" className="text-[10px] px-1.5 py-0">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}

                  <div className="mt-auto w-full">
                    <FollowButton
                      targetUserId={user.id}
                      size="sm"
                      className="w-full"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FriendsTasteSummary() {
  const { data: tasteSummary, isLoading } = useQuery<{
    cuisines: { name: string; count: number }[];
    restaurants: { name: string; reviewCount: number; avgRating: number }[];
    totalFriendReviews: number;
    friendCount: number;
  }>({
    queryKey: ['/api/friends/taste-summary'],
  });

  if (isLoading) {
    return (
      <div className="px-4 py-3">
        <Skeleton className="w-full h-24 rounded-md" />
      </div>
    );
  }

  if (!tasteSummary || (tasteSummary.cuisines.length === 0 && tasteSummary.restaurants.length === 0)) {
    return null;
  }

  return (
    <div className="px-4 py-3">
      <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-primary" />
        What Your Friends Love
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {tasteSummary.cuisines.length > 0 && (
          <Card>
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-2">
                <UtensilsCrossed className="w-4 h-4 text-primary" />
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Top Cuisines</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {tasteSummary.cuisines.map((cuisine) => (
                  <Badge key={cuisine.name} variant="secondary" className="text-xs">
                    {cuisine.name}
                    <span className="ml-1 text-muted-foreground">({cuisine.count})</span>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {tasteSummary.restaurants.length > 0 && (
          <Card>
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-2">
                <Heart className="w-4 h-4 text-primary" />
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Popular Spots</span>
              </div>
              <div className="space-y-1.5">
                {tasteSummary.restaurants.slice(0, 3).map((restaurant) => (
                  <div key={restaurant.name} className="flex items-center justify-between gap-2 text-sm">
                    <span className="truncate font-medium">{restaurant.name}</span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                      <span className="text-xs text-muted-foreground">{restaurant.avgRating}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {tasteSummary.totalFriendReviews > 0 && (
        <p className="text-xs text-muted-foreground mt-2 text-center">
          Based on {tasteSummary.totalFriendReviews} reviews from {tasteSummary.friendCount} {tasteSummary.friendCount === 1 ? 'friend' : 'friends'}
        </p>
      )}
    </div>
  );
}

function NetworkTopRestaurants() {
  const [, navigate] = useLocation();
  const [selectedCuisine, setSelectedCuisine] = useState('');

  const { data, isLoading } = useQuery<{
    restaurants: Array<{ id: string; name: string; avgRating: number; reviewCount: number; friendCount: number }>;
    cuisines: string[];
  }>({
    queryKey: [`/api/feed/top-restaurants${selectedCuisine ? `?cuisine=${encodeURIComponent(selectedCuisine)}` : ''}`],
  });

  const restaurants = data?.restaurants || [];
  const cuisines = data?.cuisines || [];

  if (isLoading) {
    return (
      <div className="px-4 py-3">
        <div className="flex items-center gap-2 mb-3">
          <Skeleton className="w-4 h-4 rounded" />
          <Skeleton className="w-48 h-4 rounded" />
        </div>
        <div className="flex gap-2 mb-3 overflow-hidden">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="w-20 h-7 rounded-full flex-shrink-0" />
          ))}
        </div>
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="w-full h-14 rounded-md" />
          ))}
        </div>
      </div>
    );
  }

  if (!data || (restaurants.length === 0 && !selectedCuisine)) {
    return null;
  }

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Crown className="w-4 h-4 text-amber-500" />;
    if (rank === 2) return <span className="w-4 h-4 flex items-center justify-center text-xs font-bold text-muted-foreground">2</span>;
    if (rank === 3) return <span className="w-4 h-4 flex items-center justify-center text-xs font-bold text-muted-foreground">3</span>;
    return <span className="w-4 h-4 flex items-center justify-center text-xs font-bold text-muted-foreground">{rank}</span>;
  };

  return (
    <div className="px-4 py-3">
      <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
        <Trophy className="w-4 h-4 text-primary" />
        Top Rated by My Network
      </h3>

      {cuisines.length > 0 && (
        <div className="flex gap-2 mb-3 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
          <Badge
            variant={selectedCuisine === '' ? 'default' : 'secondary'}
            className="cursor-pointer flex-shrink-0 toggle-elevate"
            onClick={() => setSelectedCuisine('')}
          >
            All
          </Badge>
          {cuisines.map((c) => (
            <Badge
              key={c}
              variant={selectedCuisine === c ? 'default' : 'secondary'}
              className="cursor-pointer flex-shrink-0 capitalize toggle-elevate"
              onClick={() => setSelectedCuisine(selectedCuisine === c ? '' : c)}
            >
              {c}
            </Badge>
          ))}
        </div>
      )}

      {restaurants.length === 0 ? (
        <div className="text-center py-6">
          <p className="text-sm text-muted-foreground">
            No {selectedCuisine ? selectedCuisine + ' ' : ''}restaurants reviewed by your network yet
          </p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {restaurants.map((restaurant, index) => (
            <div
              key={restaurant.id}
              className="flex items-center gap-3 p-2.5 rounded-md hover-elevate active-elevate-2 cursor-pointer"
              onClick={() => navigate(`/restaurant/${restaurant.id}`)}
              data-testid={`top-restaurant-${index + 1}`}
            >
              <div className="w-6 flex items-center justify-center flex-shrink-0">
                {getRankIcon(index + 1)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{restaurant.name}</p>
                <p className="text-xs text-muted-foreground">
                  {restaurant.friendCount} {restaurant.friendCount === 1 ? 'friend' : 'friends'} reviewed
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
                <span className="text-sm font-semibold">{restaurant.avgRating}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function getActivityContext(review: any): string | null {
  const rating = review.rating || 0;
  const restaurantName = review.restaurant?.name || '';

  if (rating >= 9) return `highly recommends ${restaurantName}`;
  if (rating >= 7) return `enjoyed ${restaurantName}`;
  if (review.tags?.length > 0) {
    const tag = review.tags[0];
    return `tried ${tag} at ${restaurantName}`;
  }
  return `reviewed ${restaurantName}`;
}

function FriendsReviewFeed() {
  const [, navigate] = useLocation();
  const [inviteOpen, setInviteOpen] = useState(false);

  const { data: rawReviews = [], isLoading } = useQuery<any[]>({
    queryKey: ['/api/feed/reviews?filterTags=following'],
  });

  const reviews = rawReviews.map((review: any) => ({
    ...review,
    user: {
      ...review.user,
      id: review.userId || review.user?.id,
      name: review.user?.name || review.user?.displayName || review.user?.username || 'Unknown',
      verified: review.user?.trustTier === 'elite' || review.user?.trustTier === 'trusted',
      followersCount: review.user?.followersCount || 0,
    },
    restaurant: {
      ...review.restaurant,
      id: review.restaurant?.id || review.restaurantId || '',
      location: review.restaurant?.location || review.restaurant?.city || review.restaurant?.address || 'Unknown location',
      city: review.restaurant?.city || 'Unknown',
      overallRating: review.restaurant?.overallRating || 8.5,
      totalReviews: review.restaurant?.totalReviews || 100,
    },
    image: getMediaUrl(review.image || review.images?.[0] || ''),
    images: review.images?.map((img: string) => getMediaUrl(img)) || [],
    items: review.items?.map((item: any) => ({
      ...item,
      image: getMediaUrl(item.image || '') || item.image,
    })),
    comments: review.commentsCount || 0,
    createdAt: review.createdAt || 'Recently',
    tags: review.tags || [],
  }));

  if (isLoading) {
    return (
      <div className="space-y-4 p-4">
        {[1, 2].map((i) => (
          <Card key={i}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Skeleton className="w-10 h-10 rounded-full" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="w-32 h-4" />
                  <Skeleton className="w-24 h-3" />
                </div>
              </div>
              <Skeleton className="w-full h-40 rounded-md" />
              <Skeleton className="w-full h-4" />
              <Skeleton className="w-3/4 h-4" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 text-center">
        <div className="w-16 h-16 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center mb-4">
          <Utensils className="w-8 h-8 text-primary" />
        </div>
        <h3 className="font-semibold text-lg mb-2">See what your friends are eating</h3>
        <p className="text-muted-foreground text-sm mb-6 max-w-xs">
          Follow people to see their restaurant reviews and recommendations right here. It's like having a food-savvy friend group in your pocket.
        </p>
        <div className="flex flex-col gap-2 w-full max-w-xs">
          <Button onClick={() => setInviteOpen(true)} className="w-full" data-testid="button-invite-empty">
            <UserPlus className="w-4 h-4 mr-2" />
            Invite friends to Eatzy
          </Button>
          <Button variant="outline" onClick={() => navigate('/')} className="w-full" data-testid="button-explore-feed">
            <Search className="w-4 h-4 mr-2" />
            Explore the feed
          </Button>
        </div>
        <InviteModal open={inviteOpen} onOpenChange={setInviteOpen} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      {reviews.map((review: any) => {
        const activityText = getActivityContext(review);
        return (
          <div key={review.id}>
            {activityText && (
              <p className="text-xs text-muted-foreground mb-1.5 px-1 flex items-center gap-1">
                <span className="font-medium text-foreground">{review.user?.name?.split(' ')[0] || 'Someone'}</span>
                {activityText.replace(review.restaurant?.name || '', '').trim()}
                {review.restaurant?.name && (
                  <span className="font-medium text-foreground">{review.restaurant.name}</span>
                )}
              </p>
            )}
            <ReviewCard {...review} />
          </div>
        );
      })}
    </div>
  );
}

export default function FriendsActivity() {
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [, navigate] = useLocation();
  const { user } = useAuth();

  const mainNetworkUserId = user?.canonicalId || user?.id;
  const { data: followingData } = useQuery<{ users: any[], total: number }>({
    queryKey: [`/api/users/${mainNetworkUserId}/following`],
    enabled: !!mainNetworkUserId,
  });

  const followingCount = followingData?.total || 0;
  const hasSmallNetwork = followingCount < 5;

  return (
    <div className="min-h-screen pb-24 flex flex-col">
      <div className="flex-1 overflow-y-auto">
        {hasSmallNetwork && (
          <div className="px-4 pt-3 pb-1">
            <button
              onClick={() => setInviteModalOpen(true)}
              className="w-full p-3 rounded-md bg-primary/5 dark:bg-primary/10 border border-primary/15 hover-elevate active-elevate-2 text-left"
              data-testid="button-invite-prompt"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground">Invite friends to Eatzy</p>
                  <p className="text-xs text-muted-foreground">See trusted reviews from people you know</p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              </div>
            </button>
          </div>
        )}

        <MyNetworkSection />

        <div className="border-t">
          <DiscoverPeopleSection />
        </div>

        <div className="border-t">
          <FriendsTasteSummary />
        </div>

        <div className="border-t">
          <NetworkTopRestaurants />
        </div>

        <div className="border-t">
          <div className="px-4 pt-3 pb-1 flex items-center justify-between gap-2">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <Star className="w-4 h-4 text-primary" />
              Friends' Reviews
            </h3>
            {!hasSmallNetwork && (
              <button
                onClick={() => setInviteModalOpen(true)}
                className="text-xs text-primary hover:underline flex items-center gap-0.5"
                data-testid="button-invite-inline"
              >
                <UserPlus className="w-3 h-3" /> Invite
              </button>
            )}
          </div>
          <FriendsReviewFeed />
        </div>
      </div>

      <InviteModal open={inviteModalOpen} onOpenChange={setInviteModalOpen} />

      <BottomNavigation
        activeTab="friends"
        onTabChange={(tab) => {
          if (tab === 'feed') navigate('/');
          else if (tab === 'menus') navigate('/restaurants');
          else if (tab === 'friends') navigate('/friends');
          else if (tab === 'profile') navigate('/profile');
        }}
      />
    </div>
  );
}
